<template>
    <b-card no-body>
        <div class="card-header pb-0">
            <div class="col-md-12 pl-0">
                <b-card-title>
                    {{ $t('Component(Text):general-info') }}
                </b-card-title>
            </div>
        </div>
        <div class="p-2">
            <validation-observer ref="generalInfo">
                <b-row>
                    <b-col :md="showDomain ? '4' : '6'" :lg="showDomain ? '4' : '6'">
                        <b-form-group
                            :label="$t('Component(Label):name')"
                            label-for="name"
                        >
                            <validation-provider
                                #default="{ errors }"
                                :name="$t('Component(Label):name')"
                                rules="required"
                            >
                                <b-form-input
                                    :state="errors.length > 0 ? false:null"
                                    id="name"
                                    v-model="title"
                                    @input="emit"
                                    :placeholder="$t('Component(Placeholder):name')"
                                />
                                <small class="text-danger">{{ errors[0] }}</small>
                            </validation-provider>
                        </b-form-group>
                    </b-col>
                    <b-col v-if="showDomain" md="4" lg="4">
                        <b-form-group
                            :label="$t('Component(Label):domain')"
                            label-for="domain"
                        >
                            <validation-provider
                                #default="{ errors }"
                                :name="$t('Component(Label):domain')"
                                rules="required"
                            >
                                <b-form-input
                                    :state="errors.length > 0 ? false:null"
                                    id="domain"
                                    v-model="domain"
                                    @input="emit"
                                    :placeholder="$t('Component(Placeholder):domain')"
                                />
                                <small class="text-danger">{{ errors[0] }}</small>
                            </validation-provider>
                        </b-form-group>
                    </b-col>
                    <b-col :md="showDomain ? '4' : '6'" :lg="showDomain ? '4' : '6'">
                        <CountrySelector
                            :selected="state"
                            @input="onCountrySet"
                        />
                    </b-col>
                </b-row>

                <b-row>
                    <b-col>
                        <b-card no-body class="mb-0">
                            <LegalIdentity
                                ref="legalIdentity"
                                :identity="legalIdentity"
                                @input="onLegalIdentityChange"
                            />
                        </b-card>
                    </b-col>
                </b-row>

                <OrganizationSelector
                    v-if="isParentVisible"
                    :selected="parent"
                    type="internal"
                    @input="onParentChange"
                />

                <BusinessRoles
                    v-if="isBusinessRolesVisible"
                    @change="onBusinessRolesChange"
                    :selected="businessRoles"
                />

                <b-row v-if="showValidFromToDates">
                    <b-col lg="6" md="6">
                        <div>
                            <label for="validFrom">{{ $t('Component(Label):valid-from') }}</label>
                            <b-form-datepicker
                                id="validFrom"
                                v-model="validFrom"
                                @input="emit"
                                :placeholder="$t('Component(Placeholder):valid-from')"
                                :locale="$store.state.component.datePicker.locale"
                                :date-format-options="$store.state.component.datePicker.format"
                                :initial-date="initialDate"
                            />
                        </div>
                    </b-col>
                    <b-col lg="6" md="6">
                        <div>
                            <label for="validTo">{{ $t('Component(Label):valid-to') }}</label>
                            <b-form-datepicker
                                id="validTo"
                                v-model="validTo"
                                :placeholder="$t('Component(Placeholder):valid-to')"
                                :locale="$store.state.component.datePicker.locale"
                                @input="emit"
                                reset-button
                                :reset-value="validToResetValue"
                                :date-format-options="$store.state.component.datePicker.format"
                                initial-date="2099-01-01"
                            />
                            <small v-if="validToLessThenValidFrom" class="text-danger">
                                {{ $t('Component(Label):valid-to') }} can't be less then {{ $t('Component(Label):valid-from') }}
                            </small>
                        </div>
                    </b-col>
                    <b-col lg="6" md="6" class="mt-2">
                        <b-form-checkbox
                            checked="true"
                            name="check-button"
                            class="mb-1"
                            @input="emit"
                            switch
                            inline
                            v-model="showProductStatistics"
                        >
                            {{ $t('Component(Label):show-product-statistics') }}
                        </b-form-checkbox>
                    </b-col>
                </b-row>
                <Location
                    v-if="showLocation && isLocationVisible"
                    :location="location"
                    class="pt-1"
                    @input="onLocationChange"
                />
            </validation-observer>
        </div>
    </b-card>
</template>

<script>
import {required} from '@validations'
import {
    BCard, BCardTitle, BFormGroup, BFormInput, BRow, BCol, BFormDatepicker, BFormCheckbox
} from 'bootstrap-vue'
import {CountrySelector, BusinessRoles, OrganizationSelector} from "@core/components/supply-chain";
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import {OrganizationType} from "@/libs/supply-chain";
import LegalIdentity from "@core/components/common/LegalIdentity.vue";
import {isSustainity} from "@/libs/application";
import Location from "@/views/administration/organization/components/Location.vue";

export default {
    components: {
        Location,
        LegalIdentity,
        BCard, BFormGroup, BFormInput, BCardTitle, BRow, BCol, ValidationProvider, ValidationObserver,
        CountrySelector, BusinessRoles, OrganizationSelector, BFormDatepicker, BFormCheckbox
    },
    props: {
        showDomain: {
            default: true,
        },
        organization: {
            type: Object,
            required: true
        },
        showValidFromToDates: {
            default: false
        },
        type: {
            required: true
        },
        isParentVisible: {
            default() {
                return this.type === OrganizationType.INTERNAL;
            }
        },
        isLocationVisible: {
            default: false
        },
    },
    data() {
        return {
            showLocation: isSustainity(),
            title: this.organization.title,
            legalIdentity: this.organization.legalIdentity,
            domain: this.organization.domain,
            state: this.organization.state,
            businessRoles: this.organization.businessRoles,
            validFrom: this.organization.validFrom,
            validTo: this.organization.validTo,
            parent: this.organization.parent,
            location: this.organization.location,
            showProductStatistics: this.organization.showProductStatistics,
            initialDate: new Date(),
            required,
        };
    },
    methods: {
        onLegalIdentityChange(identity) {
            this.legalIdentity = identity;
            this.emit();
        },

        onParentChange(parent) {
            this.parent = parent;
            this.emit();
        },

        onCountrySet(country) {
            this.state = country;
            this.emit();
        },

        onBusinessRolesChange(roles) {
            this.businessRoles = roles;
            this.emit();
        },

        onLocationChange(location) {
            this.location = location;
            this.emit();
        },

        validate() {
            return new Promise((resolve) => {
                this.$refs.generalInfo.validate().then(result => {
                    const isLegalIdentityValid = this.$refs.legalIdentity.validate();
                    resolve(result && isLegalIdentityValid);
                });
            });
        },

        emit() {
            this.$emit('input', {
                title: this.title,
                domain: this.domain,
                legalIdentity: this.legalIdentity,
                state: this.state,
                businessRoles: this.businessRoles,
                parent: this.parent,
                validFrom: this.validFrom,
                validTo: this.validTo,
                location: this.location,
                showProductStatistics: this.showProductStatistics,
            })
        }
    },
    computed: {
        isBusinessRolesVisible() {
            return this.type === OrganizationType.INTERNAL;
        },
        validToResetValue() {
            if (this.validFrom) {
                return this.$moment(this.validFrom).add(1, 'M').format('YYYY-MM-DD');
            }

            return this.$moment(this.initialDate).add(1, 'M').format('YYYY-MM-DD');
        },
        validToLessThenValidFrom() {
            if (this.validFrom) {
                if (this.validTo && !this.$moment(this.validTo).isAfter(this.validFrom)) {
                    return true;
                }
            }
            return false;
        }
    },
    watch: {
        organization(o) {
            this.title = o.title;
            this.legalIdentity = o.legalIdentity;
            this.domain = o.domain;
            this.state = o.state;
            this.businessRoles = o.businessRoles;
            this.parent = o.parent;
            this.validFrom = o.validFrom;
            this.validTo = o.validTo;
            this.location = o.location;
            this.showProductStatistics = o.showProductStatistics;
        }
    }
}
</script>

<style scoped>

</style>
